import React, { useEffect, useMemo, useState } from "react";
import { KnsaixServerClient } from "./knsaix-server-client";

/**
 * KNSAIX Repository & Capability Center — tek dosyalık olgun UI + adapter prototipi
 *
 * Hedef:
 * - GitHub ve KNSAIX Server için yalnız LOGIN bağlantısı dışarıda kalır.
 * - Login sonrası panel kendi envanterini keşfeder ve tek ekranda yönetir.
 * - Repo, tool, skill, MCP, agent, service, package, deployment, workflow ve model-adapter
 *   aynı inventory modelinde görünür.
 * - GitHub aktif kullanılır: repo/branch/commit/push/pull/PR/issues/actions/releases/security.
 * - Sunucu aktif kullanılır: repo scan, service/runtime health, install/remove/enable/disable,
 *   local workspace, deploy, rollback, logs, dependency/usage relations.
 * - Git terimleri anlaşılır Türkçe anlatılır; gerçek terim küçük ikincil etiket olarak korunur.
 * - UI stili önceki birleşik panelin kompakt koyu satır diliyle aynı tutulur.
 *
 * NOT: Bu dosya backend'e bağlanmaya hazır bir arayüz + adapter sözleşmesidir.
 *       MOCK_* verileri yalnız ekranın bağımsız çalışması içindir.
 */

const T = {
  bg: "#0F1417",
  header: "#12181B",
  surface: "#171D21",
  surface2: "#13191D",
  surface3: "#1B2227",
  border: "#2A3338",
  borderSoft: "#22292D",
  text: "#E7EAEA",
  text2: "#B3BCC0",
  muted: "#8B979C",
  faint: "#5C666B",
  accent: "#E8A33D",
  ok: "#6FCF97",
  warn: "#F2C166",
  danger: "#E85C5C",
  info: "#77A9C7",
  purple: "#9E8CFF",
};

const KIND_META = {
  repository: { label: "Repo", icon: "◫" },
  tool: { label: "Araç", icon: "⌁" },
  skill: { label: "Skill", icon: "✦" },
  mcp: { label: "MCP", icon: "↔" },
  agent: { label: "Agent", icon: "◉" },
  service: { label: "Servis", icon: "◆" },
  package: { label: "Paket", icon: "▣" },
  workflow: { label: "Akış", icon: "⇢" },
  deployment: { label: "Dağıtım", icon: "▲" },
  model_adapter: { label: "Model adaptörü", icon: "◇" },
};

const STATUS_META = {
  healthy: { label: "Sağlıklı", tone: "ok" },
  active: { label: "Aktif", tone: "ok" },
  installed: { label: "Kurulu", tone: "info" },
  disabled: { label: "Kapalı", tone: "muted" },
  degraded: { label: "Kısmi sorun", tone: "warn" },
  blocked: { label: "Engelli", tone: "fail" },
  unknown: { label: "Bilinmiyor", tone: "muted" },
  update: { label: "Güncelleme var", tone: "warn" },
};

const SYNC_META = {
  equal: { label: "GitHub ve sunucu eşit", tone: "ok", action: "Eşit" },
  github_ahead: { label: "GitHub'da yeni değişiklik var", tone: "info", action: "Sunucuya al" },
  server_ahead: { label: "Sunucuda gönderilmemiş değişiklik var", tone: "warn", action: "GitHub'a gönder" },
  diverged: { label: "İki tarafta da değişiklik var", tone: "warn", action: "Güvenli eşitle" },
  conflict: { label: "Çakışma var", tone: "fail", action: "Çakışmayı çöz" },
  github_only: { label: "Yalnız GitHub'da", tone: "info", action: "Sunucuya getir" },
  server_only: { label: "Yalnız sunucuda", tone: "warn", action: "GitHub'a bağla" },
  disconnected: { label: "Bağlantı yok", tone: "muted", action: "Bağla" },
};

const INITIAL_CONNECTIONS = {
  github: {
    connected: false,
    account: null,
    scopes: [],
    repositoryCount: 0,
    repositoryStatus: "unknown",
  },
  server: {
    connected: true,
    account: "KNSAIX Server",
    endpoint: "knsaix.local",
    node: "primary",
  },
};

export const KNSAIX_ADAPTER_CONTRACT = {
  github: [
    "login", "logout", "getSession", "listRepositories", "getRepository", "createRepository",
    "deleteRepository", "archiveRepository", "forkRepository", "listBranches", "createBranch",
    "deleteBranch", "compare", "listCommits", "commitFiles", "push", "pull", "fetch",
    "listPullRequests", "createPullRequest", "reviewPullRequest", "mergePullRequest", "closePullRequest",
    "listIssues", "createIssue", "updateIssue", "listActions", "rerunAction", "cancelAction",
    "listReleases", "createRelease", "listTags", "listPackages", "getSecurity", "getDependabot",
    "getCodeScanning", "getSecretScanning", "getRules", "updateRules", "listCollaborators",
    "updateCollaborator", "listWebhooks", "createWebhook", "deleteWebhook", "listDeployments",
    "listEnvironments", "getFile", "putFile", "deleteFile", "searchCode", "searchRepositories"
  ],
  server: [
    "login", "logout", "getSession", "scanInventory", "watchInventory", "getItem", "installItem",
    "removeItem", "enableItem", "disableItem", "updateItem", "inspectSource", "validateItem",
    "getDependencies", "getDependents", "getPermissions", "setPermissions", "getHealth", "runHealthCheck",
    "getLogs", "getMetrics", "listWorkspaces", "createWorkspace", "removeWorkspace", "getGitState",
    "gitFetch", "gitPull", "gitPush", "gitCommit", "gitBranch", "gitMerge", "gitRebase", "gitReset",
    "gitDiff", "gitStatus", "linkGithubRemote", "unlinkGithubRemote", "deploy", "rollback", "listDeployments",
    "runTests", "runWorkflow", "stopWorkflow", "listServices", "restartService", "startService", "stopService",
    "listSkills", "listTools", "listMcp", "listAgents", "listPackages", "listModelAdapters", "listWorkflows"
  ],
};

function pillTone(status) {
  return STATUS_META[status]?.tone || SYNC_META[status]?.tone || "muted";
}

function Pill({ children, tone = "muted" }) {
  const tones = {
    muted: { bg: "#1F262B", fg: T.muted, bd: T.border },
    ok: { bg: "#16211B", fg: T.ok, bd: "#233A2C" },
    warn: { bg: "#272218", fg: T.warn, bd: "#44371F" },
    fail: { bg: "#241717", fg: T.danger, bd: "#3A2323" },
    info: { bg: "#172229", fg: T.info, bd: "#29404C" },
    accent: { bg: "#2A2117", fg: T.accent, bd: "#4B3720" },
    purple: { bg: "#201D2D", fg: T.purple, bd: "#393151" },
  };
  const s = tones[tone] || tones.muted;
  return <span style={{
    background: s.bg, color: s.fg, border: `1px solid ${s.bd}`,
    fontSize: 11, padding: "2px 7px", borderRadius: 4,
    fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap"
  }}>{children}</span>;
}

function Btn({ children, onClick, tone = "default", disabled, title, compact = false }) {
  const map = {
    default: { bg: "transparent", fg: T.text2, bd: T.border },
    accent: { bg: T.accent, fg: T.bg, bd: T.accent },
    ok: { bg: "transparent", fg: T.ok, bd: "#31543E" },
    warn: { bg: "transparent", fg: T.warn, bd: "#4A3D25" },
    danger: { bg: "transparent", fg: T.danger, bd: "#4B2929" },
    info: { bg: "transparent", fg: T.info, bd: "#31505F" },
  };
  const s = map[tone] || map.default;
  return <button onClick={onClick} disabled={disabled} title={title} style={{
    background: s.bg, color: s.fg, border: `1px solid ${s.bd}`,
    borderRadius: 5, padding: compact ? "5px 8px" : "7px 10px", fontSize: 12,
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? .45 : 1,
    fontWeight: tone === "accent" ? 700 : 600
  }}>{children}</button>;
}

function Toggle({ on, onClick, disabled }) {
  return <button onClick={onClick} disabled={disabled} style={{
    width: 38, height: 20, borderRadius: 999, position: "relative", flexShrink: 0,
    background: on ? "rgba(111,207,151,.15)" : "#1A2024",
    border: `1px solid ${on ? T.ok : "#3A4247"}`,
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? .45 : 1
  }}>
    <span style={{ position: "absolute", top: 2, left: on ? 19 : 2, width: 14, height: 14,
      borderRadius: "50%", background: on ? T.ok : "#6B7478", transition: "left .15s ease" }} />
  </button>;
}

function Help({ title, children }) {
  const [open, setOpen] = useState(false);
  return <span style={{ position: "relative", display: "inline-flex" }}>
    <button onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }} style={{
      width: 18, height: 18, borderRadius: "50%", border: `1px solid ${T.border}`,
      background: T.surface2, color: T.muted, fontSize: 11, cursor: "pointer"
    }}>?</button>
    {open && <div onClick={e => e.stopPropagation()} style={{
      position: "absolute", zIndex: 30, top: 24, right: 0, width: 310,
      background: "#11171A", border: `1px solid ${T.border}`, borderRadius: 7,
      boxShadow: "0 18px 50px rgba(0,0,0,.45)", padding: 12, color: T.text2,
      fontSize: 12, lineHeight: 1.55
    }}>
      <div style={{ color: T.text, fontWeight: 700, marginBottom: 5 }}>{title}</div>
      {children}
    </div>}
  </span>;
}

function SectionTitle({ children, right }) {
  return <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
    <div style={{ fontSize: 12, letterSpacing: ".08em", color: T.muted, textTransform: "uppercase", fontWeight: 800 }}>{children}</div>
    {right}
  </div>;
}

function Stat({ label, value, tone }) {
  return <div style={{ minWidth: 100 }}>
    <div style={{ fontSize: 11, color: T.faint, marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 700, color: tone ? T[tone] : T.text }}>{value}</div>
  </div>;
}

function Empty({ children }) {
  return <div style={{ border: `1px dashed ${T.border}`, color: T.muted, padding: 18, borderRadius: 7, fontSize: 12 }}>{children}</div>;
}

function ConnectionCard({ type, connected, account, detail, onLogin, onLogout }) {
  const label = type === "github" ? "GitHub" : "KNSAIX Server";
  return <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 7, padding: 12, minWidth: 250 }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
      <div>
        <div style={{ color: T.text, fontWeight: 800, fontSize: 13 }}>{label}</div>
        <div style={{ color: connected ? T.ok : T.muted, fontSize: 11, marginTop: 3 }}>{connected ? `✓ ${account}` : "Bağlı değil"}</div>
        {detail && <div style={{ color: T.faint, fontSize: 10, marginTop: 2 }}>{detail}</div>}
      </div>
      {type === "server"
        ? <span style={{ color: connected ? T.ok : T.muted, fontSize: 10, fontWeight: 800 }}>
            {connected ? "OTOMATİK" : "BEKLENİYOR"}
          </span>
        : <Btn compact tone={connected ? "default" : "accent"} onClick={connected ? onLogout : onLogin}>{connected ? "Çıkış" : "Giriş yap"}</Btn>}
    </div>
  </div>;
}

function InventoryRow({ item, expanded, onExpand, onToggle, onAction, selected, onSelect }) {
  const meta = KIND_META[item.kind] || { label: item.kind, icon: "•" };
  const status = STATUS_META[item.status] || STATUS_META.unknown;
  const sync = item.sync ? SYNC_META[item.sync.status] : null;

  const dirtyFiles = Number(
    item.server?.dirtyFiles || 0,
  );

  const hasLocalChanges =
    item.kind === "repository"
    && dirtyFiles > 0;

  const repositoryStateLabel = (() => {
    if (!sync) {
      return null;
    }

    if (
      item.sync?.status === "equal"
      && hasLocalChanges
    ) {
      return "Commitler eşit · yerel değişiklik var";
    }

    return sync.label;
  })();

  const repositoryStateTone = (() => {
    if (
      item.sync?.status === "equal"
      && hasLocalChanges
    ) {
      return "warn";
    }

    return sync?.tone || "muted";
  })();

  const repositoryStateColor =
    repositoryStateTone === "ok"
      ? T.ok
      : repositoryStateTone === "warn"
        ? T.warn
        : repositoryStateTone === "fail"
          ? T.danger
          : T.info;

  return <div style={{ borderBottom: `1px solid ${T.borderSoft}`, background: expanded ? T.surface2 : "transparent" }}>
    <div onClick={onExpand} style={{
      display: "grid", gridTemplateColumns: "24px 1fr minmax(160px, .42fr) auto auto",
      gap: 12, alignItems: "center", padding: "11px 12px", cursor: "pointer"
    }}>
      <input type="checkbox" checked={selected} onChange={(e) => { e.stopPropagation(); onSelect(e.target.checked); }} onClick={e => e.stopPropagation()} />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ color: T.faint, fontSize: 14 }}>{meta.icon}</span>
          <span style={{ color: T.text, fontWeight: 800, fontSize: 13 }}>{item.name}</span>
          <Pill tone="muted">{meta.label}</Pill>
          <Pill tone={status.tone}>{status.label}</Pill>
          {item.update?.available && <Pill tone="warn">Güncelleme var</Pill>}
        </div>
        <div style={{ color: T.muted, fontSize: 11, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.description}</div>
      </div>
      <div style={{ minWidth: 0 }}>
        {sync ? <>
          <div
            style={{
              color: repositoryStateColor,
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {repositoryStateLabel}
          </div>
          <div style={{ color: T.faint, fontSize: 10, marginTop: 2 }}>GitHub ↔ Sunucu · {item.sync.last}</div>
        </> : <>
          <div style={{ color: T.text2, fontSize: 11 }}>{item.source === "server" ? "KNSAIX Server" : item.source === "github" ? "GitHub" : "GitHub + Server"}</div>
          <div style={{ color: T.faint, fontSize: 10, marginTop: 2 }}>{item.version || item.server?.runtime || "—"}</div>
        </>}
      </div>
      {sync && sync.action !== "Eşit" ? <Btn compact tone={sync.tone === "fail" ? "danger" : sync.tone === "warn" ? "warn" : "info"} onClick={(e) => { e.stopPropagation(); onAction("sync"); }}>{sync.action}</Btn> : <span />}
      <Toggle on={!!item.enabled} onClick={(e) => { e?.stopPropagation?.(); onToggle(); }} disabled={item.kind === "repository"} />
    </div>
    {expanded && <InventoryDetail item={item} onAction={onAction} />}
  </div>;
}

function InventoryDetail({ item, onAction }) {
  const [tab, setTab] = useState("overview");
  const tabs = ["overview", "relations", "permissions"];
  if (item.kind === "repository") tabs.splice(1, 0, "github", "server", "changes", "collaboration", "automation", "security");
  if (item.recent) tabs.push("activity");

  const labels = {
    overview: "Genel Bakış", github: "GitHub", server: "Sunucu", changes: "Değişiklikler",
    collaboration: "PR / Sorunlar", automation: "Otomasyonlar", security: "Güvenlik",
    relations: "Bağlantılar", permissions: "Yetkiler", activity: "Geçmiş"
  };

  return <div style={{ padding: "0 12px 14px 48px" }}>
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", borderTop: `1px solid ${T.borderSoft}`, paddingTop: 10, marginBottom: 12 }}>
      {tabs.map(t => <button key={t} onClick={() => setTab(t)} style={{
        border: `1px solid ${tab === t ? "#4B5960" : T.borderSoft}`,
        background: tab === t ? T.surface3 : "transparent", color: tab === t ? T.text : T.muted,
        borderRadius: 5, padding: "6px 9px", fontSize: 11, cursor: "pointer"
      }}>{labels[t]}</button>)}
    </div>

    {tab === "overview" && <OverviewTab item={item} onAction={onAction} />}
    {tab === "github" && <GithubTab item={item} onAction={onAction} />}
    {tab === "server" && <ServerTab item={item} onAction={onAction} />}
    {tab === "changes" && <ChangesTab item={item} onAction={onAction} />}
    {tab === "collaboration" && <CollabTab item={item} onAction={onAction} />}
    {tab === "automation" && <AutomationTab item={item} onAction={onAction} />}
    {tab === "security" && <SecurityTab item={item} onAction={onAction} />}
    {tab === "relations" && <RelationsTab item={item} />}
    {tab === "permissions" && <PermissionsTab item={item} onAction={onAction} />}
    {tab === "activity" && <ActivityTab item={item} />}
  </div>;
}

function OverviewTab({ item, onAction }) {
  const sync = item.sync && SYNC_META[item.sync.status];
  return <div>
    {item.kind === "repository" && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 8, marginBottom: 10 }}>
      <div style={cardStyle}><Stat label="GitHub" value={item.github ? `✓ ${item.github.owner}/${item.github.repo}` : "Bağlı değil"} tone={item.github ? "ok" : "muted"} /></div>
      <div style={cardStyle}><Stat label="Sunucu" value={item.server ? `✓ ${item.server.path}` : "Yok"} tone={item.server ? "ok" : "muted"} /></div>
      <div style={cardStyle}><Stat label="Senkronizasyon" value={sync?.label || "—"} tone={sync?.tone === "ok" ? "ok" : sync?.tone === "fail" ? "danger" : "warn"} /></div>
      <div style={cardStyle}><Stat label="Çalışma kolu" value={item.server?.branch || item.github?.branch || "—"} /></div>
    </div>}
    <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 10 }}>
      {item.kind === "repository" && <>
        <Btn tone="info" onClick={() => onAction("open_files")}>Dosyaları aç</Btn>
        <Btn onClick={() => onAction("assistant")}>KNSAIX Yardımcısı</Btn>
        <Btn onClick={() => onAction("tests")}>Testleri çalıştır</Btn>
        <Btn onClick={() => onAction("deploy")}>Dağıtımlar</Btn>
      </>}
      {item.kind !== "repository" && <>
        <Btn tone="info" onClick={() => onAction("inspect")}>Ayrıntılı incele</Btn>
        <Btn onClick={() => onAction("health")}>Sağlığı doğrula</Btn>
        {item.update?.available && <Btn tone="warn" onClick={() => onAction("update")}>Güncelle</Btn>}
        <Btn tone="danger" onClick={() => onAction("remove")}>Güvenli kaldır</Btn>
      </>}
    </div>
  </div>;
}

function GithubTab({ item, onAction }) {
  if (!item.github) return <Empty>Bu öğe henüz GitHub'a bağlı değil. <Btn compact tone="info" onClick={() => onAction("link_github")}>GitHub'a bağla</Btn></Empty>;
  return <div style={{ display: "grid", gap: 9 }}>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 8 }}>
      <div style={cardStyle}><Stat label="Repo" value={`${item.github.owner}/${item.github.repo}`} /></div>
      <div style={cardStyle}><Stat label="Görünürlük" value={item.github.visibility === "private" ? "Özel" : "Açık"} /></div>
      <div style={cardStyle}><Stat label="Varsayılan kol" value={item.github.defaultBranch} /></div>
      <div style={cardStyle}><Stat label="HEAD" value={item.github.head} /></div>
    </div>
    <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
      <Btn onClick={() => onAction("branches")}>Çalışma kolları <small>(Branches)</small></Btn>
      <Btn onClick={() => onAction("commits")}>Değişiklik geçmişi <small>(Commits)</small></Btn>
      <Btn onClick={() => onAction("releases")}>Sürümler <small>(Releases)</small></Btn>
      <Btn onClick={() => onAction("settings")}>Repo ayarları</Btn>
      <Btn tone="danger" onClick={() => onAction("unlink_github")}>Bağlantıyı kaldır</Btn>
    </div>
  </div>;
}

function ServerTab({ item, onAction }) {
  if (!item.server) return <Empty>Bu repo sunucuda yok. <Btn compact tone="info" onClick={() => onAction("clone_server")}>Sunucuya getir</Btn></Empty>;
  return <div style={{ display: "grid", gap: 9 }}>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 8 }}>
      <div style={cardStyle}><Stat label="Konum" value={item.server.path} /></div>
      <div style={cardStyle}><Stat label="Çalışma kolu" value={item.server.branch || "—"} /></div>
      <div style={cardStyle}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 12,
        }}>
          <div>
            <div style={{
              color: T.text,
              fontSize: 12,
              fontWeight: 800,
            }}>
              Yerel çalışma
            </div>
            <div style={{
              color: T.faint,
              fontSize: 10,
              marginTop: 2,
            }}>
              Henüz commit edilmemiş çalışma alanı durumu
            </div>
          </div>

          <Pill
            tone={
              item.server.worktreeStatus === "conflicted"
                ? "fail"
                : item.server.worktreeStatus === "dirty"
                  ? "warn"
                  : item.server.worktreeStatus === "clean"
                    ? "ok"
                    : "muted"
            }
          >
            {
              item.server.worktreeStatus === "conflicted"
                ? "Çakışma var"
                : item.server.worktreeStatus === "dirty"
                  ? "Yerel değişiklik var"
                  : item.server.worktreeStatus === "clean"
                    ? "Temiz"
                    : "Bilinmiyor"
            }
          </Pill>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(130px, 1fr))",
          gap: 8,
        }}>
          <Stat
            label="Toplam değişiklik"
            value={`${item.server.dirtyFiles ?? 0} dosya`}
            tone={
              item.server.dirtyFiles
                ? "warn"
                : "ok"
            }
          />

          <Stat
            label="Değiştirilen"
            value={`${item.server.trackedDirty ?? 0} dosya`}
            tone={
              item.server.trackedDirty
                ? "warn"
                : "ok"
            }
          />

          <Stat
            label="Yeni"
            value={`${item.server.untracked ?? 0} dosya`}
            tone={
              item.server.untracked
                ? "info"
                : "ok"
            }
          />

          <Stat
            label="Stage edilmiş"
            value={`${item.server.staged ?? 0} dosya`}
            tone={
              item.server.staged
                ? "info"
                : "ok"
            }
          />

          <Stat
            label="Çakışma"
            value={`${item.server.conflicted ?? 0} dosya`}
            tone={
              item.server.conflicted
                ? "fail"
                : "ok"
            }
          />
        </div>

        {Number(item.server.unstaged ?? 0) > 0 && (
          <div style={{
            color: T.faint,
            fontSize: 10,
            marginTop: 10,
          }}>
            Stage edilmemiş: {item.server.unstaged} dosya
          </div>
        )}
      </div>
      <div style={cardStyle}><Stat label="Sağlık" value={STATUS_META[item.server.health]?.label || item.server.health || "—"} tone={item.server.health === "healthy" ? "ok" : "warn"} /></div>
    </div>
    <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
      <Btn onClick={() => onAction("server_files")}>Sunucu dosyalarını aç</Btn>
      <Btn onClick={() => onAction("logs")}>Loglar</Btn>
      <Btn onClick={() => onAction("health")}>Sağlık kontrolü</Btn>
      <Btn onClick={() => onAction("restart")}>Servisleri yeniden başlat</Btn>
    </div>
  </div>;
}

function ChangesTab({ item, onAction }) {
  const s = item.sync || {};
  const server = item.server || {};
  const github =
    item.github
    || item.githubRepository
    || item.repository
    || {};

  const ahead = Number(
    s.ahead
    ?? server.ahead
    ?? 0,
  );

  const behind = Number(
    s.behind
    ?? server.behind
    ?? 0,
  );

  const dirtyFiles = Number(
    server.dirtyFiles
    ?? 0,
  );

  const worktreeStatus =
    server.worktreeStatus
    || "unknown";

  const remoteUrl =
    item.remoteUrl
    || server.remoteUrl
    || github.html_url
    || github.htmlUrl
    || github.url
    || "—";

  const branch =
    server.branch
    || github.default_branch
    || github.defaultBranch
    || "—";

  const head =
    server.head
    || "—";

  const relation =
    ahead > 0 && behind > 0
      ? "diverged"
      : ahead > 0
        ? "server_ahead"
        : behind > 0
          ? "github_ahead"
          : "equal";

  const relationLabel =
    relation === "diverged"
      ? "İki tarafta da commit var"
      : relation === "server_ahead"
        ? `Sunucu ${ahead} commit önde`
        : relation === "github_ahead"
          ? `GitHub ${behind} commit önde`
          : "Commitler eşit";

  const relationTone =
    relation === "diverged"
      ? "warn"
      : relation === "server_ahead"
        ? "warn"
        : relation === "github_ahead"
          ? "info"
          : "ok";

  const worktreeLabel =
    worktreeStatus === "conflicted"
      ? "Çakışma var"
      : worktreeStatus === "dirty"
        ? `${dirtyFiles} yerel dosya değişmiş`
        : worktreeStatus === "clean"
          ? "Çalışma alanı temiz"
          : "Durum bilinmiyor";

  const worktreeTone =
    worktreeStatus === "conflicted"
      ? "fail"
      : worktreeStatus === "dirty"
        ? "warn"
        : worktreeStatus === "clean"
          ? "ok"
          : "muted";

  const sideCard = {
    ...cardStyle,
    minWidth: 0,
    padding: 14,
  };

  const valueStyle = {
    color: T.text,
    fontSize: 11,
    lineHeight: 1.45,
    overflowWrap: "anywhere",
  };

  const labelStyle = {
    color: T.faint,
    fontSize: 9,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: ".08em",
    marginBottom: 3,
  };

  return (
    <div style={{
      display: "grid",
      gap: 12,
    }}>

      <SectionTitle
        right={
          <Pill tone={relationTone}>
            {relationLabel}
          </Pill>
        }
      >
        Repository eşitleme
      </SectionTitle>

      <div style={{
        display: "grid",
        gridTemplateColumns:
          "repeat(auto-fit, minmax(260px, 1fr))",
        gap: 10,
      }}>

        <div style={sideCard}>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            marginBottom: 14,
          }}>
            <div>
              <div style={{
                color: T.text,
                fontSize: 13,
                fontWeight: 900,
              }}>
                GitHub tarafı
              </div>

              <div style={{
                color: T.faint,
                fontSize: 10,
                marginTop: 2,
              }}>
                Uzak repository
              </div>
            </div>

            <Pill tone="info">
              GitHub
            </Pill>
          </div>

          <div style={{
            display: "grid",
            gap: 11,
          }}>
            <div>
              <div style={labelStyle}>
                Repository
              </div>
              <div style={valueStyle}>
                {item.name || "—"}
              </div>
            </div>

            <div>
              <div style={labelStyle}>
                Remote
              </div>
              <div style={valueStyle}>
                {remoteUrl}
              </div>
            </div>

            <div>
              <div style={labelStyle}>
                Commit durumu
              </div>
              <div style={valueStyle}>
                {
                  behind > 0
                    ? `${behind} commit sunucuya alınmayı bekliyor`
                    : ahead > 0
                      ? `${ahead} commit sunucudan gönderilmeyi bekliyor`
                      : "Sunucu commitleriyle eşit"
                }
              </div>
            </div>
          </div>
        </div>


        <div style={sideCard}>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            marginBottom: 14,
          }}>
            <div>
              <div style={{
                color: T.text,
                fontSize: 13,
                fontWeight: 900,
              }}>
                Sunucu tarafı
              </div>

              <div style={{
                color: T.faint,
                fontSize: 10,
                marginTop: 2,
              }}>
                KNSAIX Server çalışma kopyası
              </div>
            </div>

            <Pill tone={worktreeTone}>
              {worktreeLabel}
            </Pill>
          </div>

          <div style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(110px, 1fr))",
            gap: 10,
          }}>
            <Stat
              label="Branch"
              value={branch}
              tone="muted"
            />

            <Stat
              label="HEAD"
              value={head}
              tone="muted"
            />

            <Stat
              label="Yerel değişiklik"
              value={`${dirtyFiles} dosya`}
              tone={
                dirtyFiles
                  ? "warn"
                  : "ok"
              }
            />

            <Stat
              label="Çakışma"
              value={`${server.conflicted ?? 0} dosya`}
              tone={
                server.conflicted
                  ? "fail"
                  : "ok"
              }
            />
          </div>

          <div style={{
            marginTop: 12,
          }}>
            <div style={labelStyle}>
              Sunucu yolu
            </div>

            <div style={valueStyle}>
              {server.path || "—"}
            </div>
          </div>
        </div>
      </div>


      <div style={{
        ...cardStyle,
        padding: 14,
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 12,
        }}>
          <div>
            <div style={{
              color: T.text,
              fontWeight: 900,
              fontSize: 12,
            }}>
              GitHub ↔ KNSAIX Server
            </div>

            <div style={{
              color: T.faint,
              fontSize: 10,
              marginTop: 2,
            }}>
              Önce güvenlik planı oluşturulur.
              Uygulanabilir değilse hiçbir Git işlemi çalıştırılmaz.
            </div>
          </div>

          <Pill tone={relationTone}>
            {relationLabel}
          </Pill>
        </div>


        <div style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(130px, 1fr))",
          gap: 8,
          marginBottom: 12,
        }}>
          <Stat
            label="Sunucu önde"
            value={`${ahead} commit`}
            tone={
              ahead
                ? "warn"
                : "ok"
            }
          />

          <Stat
            label="GitHub önde"
            value={`${behind} commit`}
            tone={
              behind
                ? "info"
                : "ok"
            }
          />

          <Stat
            label="Değiştirilen"
            value={`${server.trackedDirty ?? 0} dosya`}
            tone={
              server.trackedDirty
                ? "warn"
                : "ok"
            }
          />

          <Stat
            label="Yeni"
            value={`${server.untracked ?? 0} dosya`}
            tone={
              server.untracked
                ? "info"
                : "ok"
            }
          />

          <Stat
            label="Stage"
            value={`${server.staged ?? 0} dosya`}
            tone={
              server.staged
                ? "info"
                : "ok"
            }
          />
        </div>


        <div style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
        }}>
          <Btn
            onClick={() =>
              onAction("diff")
            }
          >
            Değişiklikleri göster
          </Btn>

          <Btn
            tone="info"
            onClick={() =>
              onAction("pull")
            }
          >
            GitHub'dan al
          </Btn>

          <Btn
            tone="warn"
            onClick={() =>
              onAction("push")
            }
          >
            GitHub'a gönder
          </Btn>

          <Btn
            tone={
              worktreeStatus === "conflicted"
                ? "danger"
                : "ok"
            }
            onClick={() =>
              onAction("safe_sync")
            }
          >
            Güvenli eşitle
          </Btn>
        </div>


        {worktreeStatus === "dirty" && (
          <div style={{
            marginTop: 12,
            padding: "9px 10px",
            borderRadius: 9,
            border: `1px solid ${T.warn}55`,
            color: T.warn,
            fontSize: 10,
            lineHeight: 1.45,
          }}>
            Sunucuda commit edilmemiş yerel değişiklikler var.
            Eşitleme düğmesine basabilirsin; güvenlik planı
            işlemin neden uygulanabildiğini veya engellendiğini
            gösterecek.
          </div>
        )}
      </div>
    </div>
  );
}

function CollabTab({ item, onAction }) {
  return <div style={{ display: "grid", gap: 9 }}>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 8 }}>
      <div style={cardStyle}><Stat label="Açık değişiklik önerisi" value={`${item.github?.openPRs || 0}`} /></div>
      <div style={cardStyle}><Stat label="Açık görev / sorun" value={`${item.github?.openIssues || 0}`} /></div>
    </div>
    <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
      <Btn onClick={() => onAction("prs")}>Değişiklik önerileri <small>(Pull Requests)</small></Btn>
      <Btn onClick={() => onAction("issues")}>Görevler / sorunlar <small>(Issues)</small></Btn>
      <Btn tone="info" onClick={() => onAction("new_pr")}>Yeni değişiklik önerisi</Btn>
      <Btn onClick={() => onAction("review")}>Kod incelemesi</Btn>
    </div>
  </div>;
}

function AutomationTab({ item, onAction }) {
  const a = item.github?.actions;
  return <div>
    <div style={{ ...cardStyle, display: "flex", gap: 24, flexWrap: "wrap" }}>
      <Stat label="GitHub Actions" value={a?.status === "passing" ? "Kontroller geçiyor" : "Başarısız kontrol var"} tone={a?.status === "passing" ? "ok" : "danger"} />
      <Stat label="Son çalışmalar" value={`${a?.runs || 0}`} />
    </div>
    <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 9 }}>
      <Btn onClick={() => onAction("actions")}>Otomasyonları aç</Btn>
      <Btn tone="warn" onClick={() => onAction("rerun_failed")}>Başarısızları tekrar çalıştır</Btn>
      <Btn onClick={() => onAction("workflows")}>KNSAIX akışları</Btn>
    </div>
  </div>;
}

function SecurityTab({ item, onAction }) {
  const s = item.github?.security || {};
  return <div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 8 }}>
      <div style={cardStyle}><Stat label="Gizli anahtar uyarısı" value={`${s.secretAlerts || 0}`} tone={s.secretAlerts ? "danger" : "ok"} /></div>
      <div style={cardStyle}><Stat label="Bağımlılık uyarısı" value={`${s.dependencyAlerts || 0}`} tone={s.dependencyAlerts ? "warn" : "ok"} /></div>
      <div style={cardStyle}><Stat label="Kod tarama uyarısı" value={`${s.codeAlerts || 0}`} tone={s.codeAlerts ? "danger" : "ok"} /></div>
    </div>
    <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginTop: 9 }}>
      <Btn onClick={() => onAction("security")}>Güvenlik ayrıntıları</Btn>
      <Btn onClick={() => onAction("rules")}>Ana kol koruması</Btn>
      <Btn onClick={() => onAction("dependabot")}>Bağımlılık güvenliği</Btn>
    </div>
  </div>;
}

function RelationsTab({ item }) {
  const r = item.relations || { uses: [], usedBy: [] };
  return <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 8 }}>
    <div style={cardStyle}>
      <div style={miniTitle}>Kullandıkları</div>
      {(r.uses || []).length ? r.uses.map(x => <div key={x} style={listItem}>{x}</div>) : <div style={muted}>Yok</div>}
    </div>
    <div style={cardStyle}>
      <div style={miniTitle}>Bunu kullananlar</div>
      {(r.usedBy || []).length ? r.usedBy.map(x => <div key={x} style={listItem}>{x}</div>) : <div style={muted}>Yok</div>}
    </div>
  </div>;
}

function PermissionsTab({ item, onAction }) {
  return <div>
    <div style={cardStyle}>
      {(item.permissions || []).map(p => <div key={p} style={listItem}>{p}</div>)}
      {!(item.permissions || []).length && <div style={muted}>Tanımlı özel yetki yok.</div>}
    </div>
    <div style={{ marginTop: 8 }}><Btn onClick={() => onAction("permissions")}>Yetkileri düzenle</Btn></div>
  </div>;
}

function ActivityTab({ item }) {
  return <div style={cardStyle}>{(item.recent || []).map((r, i) => <div key={i} style={{ ...listItem, display: "flex", justifyContent: "space-between", gap: 14 }}><span>{r.title}</span><span style={{ color: T.faint }}>{r.at}</span></div>)}</div>;
}

function AddDrawer({ open, onClose, onCreate }) {
  const [mode, setMode] = useState("github");
  const [value, setValue] = useState("");
  if (!open) return null;
  return <div style={overlay}><div style={drawer}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
      <div><div style={{ color: T.text, fontWeight: 900 }}>Ekle / Kur</div><div style={{ color: T.muted, fontSize: 11, marginTop: 3 }}>Repo, araç, skill, MCP, agent veya paket ekle.</div></div>
      <Btn compact onClick={onClose}>Kapat</Btn>
    </div>
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 12 }}>
      {[
        ["github", "GitHub reposu"], ["server", "Sunucu yolu"], ["catalog", "Katalog"], ["local", "Yerel paket"]
      ].map(([k,l]) => <button key={k} onClick={() => setMode(k)} style={tabBtn(mode === k)}>{l}</button>)}
    </div>
    <div style={{ color: T.text2, fontSize: 12, marginBottom: 6 }}>{mode === "github" ? "GitHub repository adresi" : mode === "server" ? "Sunucudaki yol / repository" : mode === "catalog" ? "Araç veya capability adı" : "Dosya / paket konumu"}</div>
    <input value={value} onChange={e => setValue(e.target.value)} placeholder={mode === "github" ? "github.com/owner/repo" : "Ara..."} style={inputStyle} />
    <div style={{ marginTop: 10, padding: 10, border: `1px solid ${T.borderSoft}`, borderRadius: 6, color: T.muted, fontSize: 11, lineHeight: 1.55 }}>
      KNSAIX önce kaynağı inceler, mevcut envanterle karşılaştırır, bağımlılık/yetki/çakışma analizi yapar. Kurulum varsayılan olarak doğrulama geçmeden etkinleşmez.
    </div>
    <div style={{ display: "flex", gap: 7, justifyContent: "flex-end", marginTop: 12 }}><Btn onClick={onClose}>Vazgeç</Btn><Btn tone="accent" disabled={!value.trim()} onClick={() => onCreate({ mode, value })}>İncele ve ekle</Btn></div>
  </div></div>;
}

function AssistantPanel({ open, onClose, currentItem }) {
  const [text, setText] = useState("");
  if (!open) return null;
  const suggestions = currentItem?.kind === "repository"
    ? ["Bu repo ne yapıyor?", "Son değişiklikleri açıkla", "Neden test başarısız?", "GitHub ile güvenli eşitle", "Bu değişiklik için PR hazırla"]
    : ["Bu araç ne işe yarıyor?", "Kim kullanıyor?", "Kaldırırsam ne etkilenir?", "Güncellemek güvenli mi?"];
  return <div style={{ position: "fixed", right: 18, bottom: 18, width: 390, maxWidth: "calc(100vw - 36px)", zIndex: 50, background: "#11171A", border: `1px solid ${T.border}`, borderRadius: 9, boxShadow: "0 24px 70px rgba(0,0,0,.55)" }}>
    <div style={{ padding: 11, borderBottom: `1px solid ${T.borderSoft}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div><div style={{ color: T.text, fontWeight: 900 }}>KNSAIX Yardımcısı</div><div style={{ color: T.faint, fontSize: 10 }}>{currentItem ? `${currentItem.name} bağlamı açık` : "Tüm çalışma alanı bağlamı"}</div></div>
      <Btn compact onClick={onClose}>×</Btn>
    </div>
    <div style={{ padding: 11 }}>
      <div style={{ color: T.muted, fontSize: 11, marginBottom: 7 }}>Kod, GitHub, sunucu, araçlar, servis sağlığı ve ilişkileri birlikte değerlendirebilir.</div>
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>{suggestions.map(s => <button key={s} onClick={() => setText(s)} style={chipBtn}>{s}</button>)}</div>
      <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Ne yapmak istediğini yaz…" style={{ ...inputStyle, minHeight: 92, resize: "vertical" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
        <span style={{ color: T.faint, fontSize: 10 }}>Yüksek etkili işlemler uygulanmadan önce değişiklik özeti gösterilir.</span>
        <Btn tone="accent" disabled={!text.trim()} onClick={() => setText("")}>Gönder</Btn>
      </div>
    </div>
  </div>;
}

function ConfirmModal({
  modal,
  onClose,
  onConfirm,
  onModalChange,
}) {
  if (!modal) return null;

  const repositoryChanges =
    modal.repositoryChanges
    && Array.isArray(modal.repositoryChanges.files)
      ? modal.repositoryChanges
      : null;

  const changeFiles =
    repositoryChanges?.files || [];

  const selectedFiles =
    Array.isArray(modal.selectedFiles)
      ? modal.selectedFiles
      : [];

  const updateModal = (patch) => {
    if (onModalChange) {
      onModalChange({
        ...modal,
        ...patch,
      });
    }
  };

  const toggleFile = (path) => {
    const next = selectedFiles.includes(path)
      ? selectedFiles.filter(
          (item) => item !== path
        )
      : [
          ...selectedFiles,
          path,
        ];

    updateModal({
      selectedFiles: next,
    });
  };

  if (repositoryChanges) {
    const tracked = changeFiles.filter(
      (item) => item?.kind !== "untracked"
    );

    const untracked = changeFiles.filter(
      (item) => item?.kind === "untracked"
    );

    const select = (items) => {
      updateModal({
        selectedFiles: items
          .map((item) => item?.path)
          .filter(Boolean),
      });
    };

    return (
      <div style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
      }}>
        <div style={{
          width: "min(920px, 96vw)",
          maxHeight: "90vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          borderRadius: 18,
          background: T.panel,
          border: `1px solid ${T.border}`,
          boxShadow:
            "0 24px 80px rgba(0,0,0,.32)",
        }}>
          <div style={{
            padding: "18px 20px 12px",
            borderBottom:
              `1px solid ${T.border}`,
          }}>
            <div style={{
              fontSize: 16,
              fontWeight: 800,
              color: T.text,
            }}>
              Yerel değişiklikler
            </div>

            <div style={{
              fontSize: 11,
              color: T.faint,
              marginTop: 4,
            }}>
              Commit edilecek dosyaları seç.
              Seçilmeyen dosyalara dokunulmaz.
            </div>
          </div>

          <div style={{
            padding: "12px 20px",
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            borderBottom:
              `1px solid ${T.border}`,
          }}>
            <Btn
              compact
              onClick={() => select(changeFiles)}
            >
              Tümünü seç
            </Btn>

            <Btn
              compact
              onClick={() => select(tracked)}
            >
              Değiştirilenler ({tracked.length})
            </Btn>

            <Btn
              compact
              onClick={() => select(untracked)}
            >
              Yeni dosyalar ({untracked.length})
            </Btn>

            <Btn
              compact
              onClick={() =>
                updateModal({
                  selectedFiles: [],
                })
              }
            >
              Seçimi temizle
            </Btn>

            <div style={{
              marginLeft: "auto",
              color: T.faint,
              fontSize: 11,
              alignSelf: "center",
            }}>
              {selectedFiles.length} / {changeFiles.length} seçili
            </div>
          </div>

          <div style={{
            overflow: "auto",
            padding: "8px 20px",
            flex: 1,
          }}>
            {changeFiles.map((item, index) => {
              const path = item?.path || "";
              const checked =
                selectedFiles.includes(path);

              return (
                <label
                  key={`${path}-${index}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 4px",
                    borderBottom:
                      `1px solid ${T.border}`,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      toggleFile(path)
                    }
                  />

                  <span style={{
                    width: 28,
                    color:
                      item?.kind === "untracked"
                        ? T.info
                        : T.warn,
                    fontSize: 11,
                    fontWeight: 800,
                  }}>
                    {item?.code || "?"}
                  </span>

                  <span style={{
                    color: T.text,
                    fontSize: 11,
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, monospace",
                    overflowWrap: "anywhere",
                  }}>
                    {path}
                  </span>
                </label>
              );
            })}
          </div>

          <div style={{
            padding: "14px 20px",
            borderTop:
              `1px solid ${T.border}`,
          }}>
            <input
              value={modal.commitMessage || ""}
              maxLength={200}
              placeholder="Commit mesajı"
              onChange={(event) =>
                updateModal({
                  commitMessage:
                    event.target.value,
                })
              }
              style={{
                width: "100%",
                boxSizing: "border-box",
                borderRadius: 10,
                border:
                  `1px solid ${T.border}`,
                background: T.bg,
                color: T.text,
                padding: "11px 12px",
                outline: "none",
                fontSize: 12,
              }}
            />

            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 12,
            }}>
              <Btn onClick={onClose}>
                Kapat
              </Btn>

              <Btn
                tone="accent"
                onClick={onConfirm}
                disabled={
                  selectedFiles.length === 0
                  || !(modal.commitMessage || "").trim()
                }
              >
                Seçilenleri commit et
              </Btn>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      zIndex: 9999,
      background: "rgba(0,0,0,.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 18,
    }}>
      <div style={{
        width: "min(620px, 94vw)",
        borderRadius: 18,
        background: T.panel,
        border: `1px solid ${T.border}`,
        boxShadow:
          "0 24px 80px rgba(0,0,0,.32)",
        padding: 18,
      }}>
        <div style={{
          color: T.text,
          fontSize: 15,
          fontWeight: 800,
        }}>
          {modal.title}
        </div>

        {(modal.body || modal.message) && (
          <div style={{
            color: T.muted,
            fontSize: 12,
            whiteSpace: "pre-wrap",
            marginTop: 10,
            lineHeight: 1.55,
          }}>
            {modal.body || modal.message}
          </div>
        )}

        <div style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 7,
          marginTop: 14,
        }}>
          {!modal.readOnly && (
            <Btn onClick={onClose}>
              Vazgeç
            </Btn>
          )}

          <Btn
            tone={
              modal.danger
                ? "danger"
                : "accent"
            }
            onClick={
              modal.readOnly
                ? onClose
                : onConfirm
            }
          >
            {modal.confirm || "Uygula"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

export default function KnsaixRepositoryCapabilityCenter({ authenticatedUser }) {
  const [connections, setConnections] = useState({
    github: {
      connected: false,
      account: null,
      scopes: [],
    },
    server: {
      connected: false,
      account: authenticatedUser?.display_name || "KNSAIX Server",
      endpoint: "KNSAIX yönetim servisi",
      node: "primary",
    },
  });
  const [items, setItems] = useState([]);
  const [activity, setActivity] = useState([]);
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("all");
  const [source, setSource] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [selected, setSelected] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantItem, setAssistantItem] = useState(null);
  const [modal, setModal] = useState(null);
  const [scanState, setScanState] = useState("idle");
  const [scanError, setScanError] = useState(null);
  const [primaryView, setPrimaryView] = useState("repositories");

  const serverClient = useMemo(
    () => new KnsaixServerClient(),
    [],
  );

  const filtered = useMemo(() => items.filter(i => {
    const q = query.trim().toLowerCase();
    const hit = !q || `${i.name} ${i.description} ${i.kind} ${(i.relations?.uses || []).join(" ")} ${(i.relations?.usedBy || []).join(" ")}`.toLowerCase().includes(q);

    const primary =
      primaryView === "repositories"
        ? i.kind === "repository"
        : primaryView === "skills"
          ? i.kind === "skill"
          : [
              "tool",
              "mcp",
            ].includes(i.kind);

    const k = kind === "all" || i.kind === kind;
    const src = source === "all" || i.source === source || (source === "github" && !!i.github) || (source === "server" && !!i.server);

    return hit && primary && k && src;
  }), [items, query, kind, source, primaryView]);

  const counts = useMemo(() => ({
    repos: items.filter(i => i.kind === "repository").length,
    tools: items.filter(i => i.kind !== "repository").length,
    issues: items.filter(i => ["degraded", "blocked"].includes(i.status)).length,
    updates: items.filter(i => i.update?.available).length,
  }), [items]);

  const addActivity = (tone, title, detail) => setActivity(a => [{ id: Date.now(), tone, title, detail, at: "şimdi" }, ...a].slice(0, 40));

  async function refreshGithubConnection() {
    try {
      const response = await fetch(
        "/api/v1/repository-center/github/status",
        {
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(
          `github_status_http_${response.status}`
        );
      }

      const payload = await response.json();

      setConnections(c => ({
        ...c,
        github: {
          connected: Boolean(payload?.connected),
          account: payload?.account || null,
          scopes: [],
          repositoryCount:
            Number(payload?.repository_count || 0),
          repositoryStatus:
            payload?.repository_status || "unknown",
          installationId:
            payload?.installation_id || null,
        },
      }));

      return payload;
    } catch (error) {
      setConnections(c => ({
        ...c,
        github: {
          ...c.github,
          connected: false,
          repositoryStatus: "unavailable",
        },
      }));

      return null;
    }
  }

  useEffect(() => {
    void refreshGithubConnection();
  }, []);

  const login = (type) => {
    if (type === "server") {
      void scan();
      return;
    }

    window.location.assign(
      "/api/v1/repository-center/github/manifest/start",
    );
  };

  const logout = (type) => {
    if (type === "server") return;

    addActivity(
      "info",
      "GitHub bağlantısı",
      "GitHub App kaldırma işlemi GitHub installation yönetiminden yapılır.",
    );
  };

  const normalizeInventory = (payload) => {
    if (Array.isArray(payload)) return payload;

    if (Array.isArray(payload?.items)) {
      return payload.items;
    }

    if (Array.isArray(payload?.inventory)) {
      return payload.inventory;
    }

    const groups = {
      repositories: "repository",
      repos: "repository",
      tools: "tool",
      skills: "skill",
      mcps: "mcp",
      mcp: "mcp",
      agents: "agent",
      services: "service",
      containers: "service",
      packages: "package",
      workflows: "workflow",
      deployments: "deployment",
      model_adapters: "model_adapter",
    };

    const result = [];

    for (const [key, inferredKind] of Object.entries(groups)) {
      const values = payload?.[key];

      if (!Array.isArray(values)) continue;

      for (const value of values) {
        if (!value || typeof value !== "object") continue;

        result.push({
          ...value,
          kind: value.kind || inferredKind,
        });
      }
    }

    return result;
  };

  const normalizeGithubRepositories = (payload) => {
    const repositories =
      Array.isArray(payload?.repositories)
        ? payload.repositories
        : [];

    return repositories
      .filter(
        repo =>
          repo &&
          typeof repo === "object",
      )
      .map(repo => {
        const owner =
          repo?.owner?.login ||
          (
            typeof repo?.full_name === "string"
              ? repo.full_name.split("/")[0]
              : null
          );

        const repoName =
          repo?.name ||
          (
            typeof repo?.full_name === "string"
              ? repo.full_name.split("/").pop()
              : null
          ) ||
          "repository";

        const fullName =
          repo?.full_name ||
          (
            owner
              ? `${owner}/${repoName}`
              : repoName
          );

        return {
          id:
            `github-repository-${repo?.id || fullName}`,
          kind: "repository",
          name: fullName,
          description:
            "GitHub App üzerinden keşfedilen repository.",
          status:
            repo?.archived || repo?.disabled
              ? "disabled"
              : "healthy",
          enabled:
            !repo?.archived &&
            !repo?.disabled,
          source: "github",
          github: {
            id: repo?.id || null,
            nodeId: repo?.node_id || null,
            owner,
            repo: repoName,
            fullName,
            visibility:
              repo?.private
                ? "private"
                : "public",
            private: Boolean(repo?.private),
            archived: Boolean(repo?.archived),
            disabled: Boolean(repo?.disabled),
            defaultBranch:
              repo?.default_branch || null,
            branch:
              repo?.default_branch || null,
            url:
              repo?.html_url || null,
          },
          sync: {
            status: "github_only",
            ahead: 0,
            behind: 0,
            conflicts: 0,
            last: null,
          },
          relations: {
            uses: [],
            usedBy: [],
          },
          capabilities: ["repo"],
          permissions: [],
        };
      });
  };

  const repositoryIdentityCandidates = item => {
    if (
      !item ||
      item.kind !== "repository"
    ) {
      return [];
    }

    const values = [];

    const push = value => {
      if (
        typeof value !== "string" ||
        !value.trim()
      ) {
        return;
      }

      const normalized =
        value
          .trim()
          .replace(/\.git$/i, "")
          .replace(/^https?:\/\/github\.com\//i, "")
          .replace(/^git@github\.com:/i, "")
          .replace(/\/+$/, "")
          .toLowerCase();

      if (normalized) {
        values.push(normalized);

        const basename =
          normalized.split("/").pop();

        if (basename) {
          values.push(basename);
        }
      }
    };

    push(item?.github?.fullName);
    push(item?.github?.full_name);

    if (
      item?.github?.owner &&
      item?.github?.repo
    ) {
      push(
        `${item.github.owner}/${item.github.repo}`,
      );
    }

    push(item?.github?.url);
    push(item?.server?.remote);
    push(item?.server?.remoteUrl);
    push(item?.server?.remote_url);
    push(item?.server?.path);
    push(item?.path);
    push(item?.repo);
    push(item?.name);

    return [...new Set(values)];
  };

  const mergeServerAndGithubInventory = (
    serverItems,
    githubItems,
  ) => {
    const result =
      Array.isArray(serverItems)
        ? [...serverItems]
        : [];

    for (const githubItem of githubItems) {
      const githubIds =
        repositoryIdentityCandidates(
          githubItem,
        );

      let matchIndex = -1;

      for (
        let index = 0;
        index < result.length;
        index += 1
      ) {
        const serverItem =
          result[index];

        if (
          serverItem?.kind !==
          "repository"
        ) {
          continue;
        }

        const serverIds =
          repositoryIdentityCandidates(
            serverItem,
          );

        const matched =
          serverIds.some(
            value =>
              githubIds.includes(value),
          );

        if (matched) {
          matchIndex = index;
          break;
        }
      }

      if (matchIndex < 0) {
        result.push(githubItem);
        continue;
      }

      const serverItem =
        result[matchIndex];

      result[matchIndex] = {
        ...serverItem,
        source: "both",
        github: {
          ...(serverItem?.github || {}),
          ...(githubItem?.github || {}),
        },

        // Do not claim equal/ahead/behind until
        // both Git heads are compared explicitly.
        sync:
          serverItem?.sync ||
          {
            status: "unknown",
            ahead: 0,
            behind: 0,
            conflicts: 0,
            last: null,
          },

        capabilities: [
          ...new Set([
            ...(
              Array.isArray(
                serverItem?.capabilities,
              )
                ? serverItem.capabilities
                : []
            ),
            ...(
              Array.isArray(
                githubItem?.capabilities,
              )
                ? githubItem.capabilities
                : []
            ),
          ]),
        ],
      };
    }

    return result;
  };


  function resolveRepositoryOperation(action, item) {
    if (
      action === "pull"
      || action === "push"
      || action === "safe_sync"
    ) {
      return action;
    }

    if (action !== "sync") {
      return null;
    }

    const ahead = Number(
      item?.server?.ahead
      ?? item?.sync?.ahead
      ?? 0
    );

    const behind = Number(
      item?.server?.behind
      ?? item?.sync?.behind
      ?? 0
    );

    if (ahead > 0 && behind > 0) {
      return "safe_sync";
    }

    if (behind > 0) {
      return "pull";
    }

    if (ahead > 0) {
      return "push";
    }

    return "safe_sync";
  }


  async function handleRepositoryDiffAction(
    item,
  ) {
    if (!item?.id) {
      return;
    }

    setModal({
      title: "Değişiklikler yükleniyor",
      message:
        "Sunucudaki gerçek Git çalışma alanı okunuyor.",
      readOnly: true,
      confirm: "Bekleyin",
    });

    try {
      const response = await fetch(
        `/api/v1/repository-center/repositories/${encodeURIComponent(item.id)}/changes`,
        {
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        },
      );

      let payload = {};

      try {
        payload = await response.json();
      } catch {
        payload = {};
      }

      if (!response.ok) {
        throw new Error(
          payload?.detail
          || payload?.message
          || `changes_http_${response.status}`
        );
      }

      const files = Array.isArray(
        payload?.files
      )
        ? payload.files
        : [];

      const visible = files.slice(
        0,
        120,
      );

      const lines = [
        `${payload?.repositoryName || item.name} · ${payload?.branch || "?"}`,
        `HEAD: ${payload?.head || "?"}`,
        "",
        `Toplam yerel değişiklik: ${payload?.dirtyFiles ?? 0}`,
        `Değiştirilen: ${payload?.trackedDirty ?? 0}`,
        `Yeni: ${payload?.untracked ?? 0}`,
        `Stage: ${payload?.staged ?? 0}`,
        `Çakışma: ${payload?.conflicted ?? 0}`,
        "",
        "DOSYALAR",
        ...visible.map(
          (entry) =>
            `${entry.code || "  "}  ${entry.path}`
        ),
      ];

      if (files.length > visible.length) {
        lines.push(
          "",
          `… ${files.length - visible.length} dosya daha var.`
        );
      }

      if (payload?.diffStat) {
        lines.push(
          "",
          "DIFF ÖZETİ",
          payload.diffStat,
        );
      }

      const canCommit =
        Number(payload?.dirtyFiles || 0) > 0
        && Number(payload?.conflicted || 0) === 0;

      lines.push(
        "",
        canCommit
          ? (
              "Commit et düğmesi tüm bu yerel "
              + "değişiklikleri tek commit'e alır. "
              + "GitHub'a gönderme ayrı adımdır."
            )
          : (
              Number(payload?.conflicted || 0) > 0
                ? "Çakışma çözülmeden commit yapılamaz."
                : "Commit edilecek yerel değişiklik yok."
            )
      );

      setModal({
        repositoryCommitItem: item,
        repositoryChanges: { files },
        selectedFiles: [],
        commitMessage: "",

        title: "Yerel değişiklikler",
        message: lines.join("\n"),
        readOnly: !canCommit,
        confirm:
          canCommit
            ? "Commit et"
            : "Kapat",
        danger:
          Number(payload?.conflicted || 0) > 0,
        repositoryCommitItem:
          canCommit
            ? item
            : null,
        repositoryChanges:
          payload,
      });

    } catch (error) {
      setModal({
        title:
          "Değişiklikler alınamadı",
        message:
          error?.message
          || "Git çalışma alanı okunamadı.",
        readOnly: true,
        confirm: "Kapat",
        danger: true,
      });
    }
  }

  function repositoryOperationLabel(operation) {
    if (operation === "pull") {
      return "GitHub'dan al";
    }

    if (operation === "push") {
      return "GitHub'a gönder";
    }

    if (operation === "safe_sync") {
      return "Güvenli eşitle";
    }

    return operation || "İşlem";
  }

  function repositoryBlockerText(code) {
    const labels = {
      WORKTREE_DIRTY:
        "Commit edilmemiş yerel değişiklikler var.",

      WORKTREE_CONFLICTED:
        "Çalışma alanında Git çakışması var.",

      NOTHING_TO_PULL:
        "GitHub'dan alınacak yeni commit yok.",

      NOTHING_TO_PUSH:
        "GitHub'a gönderilecek yeni commit yok.",

      LOCAL_COMMITS_PRESENT:
        "Sunucuda GitHub'da olmayan commitler var.",

      REMOTE_COMMITS_PRESENT:
        "GitHub'da sunucuda olmayan commitler var.",

      ALREADY_COMMIT_EQUAL:
        "Sunucu ve GitHub commitleri zaten eşit.",

      UNSUPPORTED_OPERATION:
        "Bu işlem desteklenmiyor.",
    };

    return (
      labels[code]
      || code
      || "Bilinmeyen engel"
    );
  }

  async function planRepositoryOperation(
    item,
    operation,
  ) {
    if (!item?.id) {
      throw new Error(
        "repository_id_missing"
      );
    }

    const response = await fetch(
      `/api/v1/repository-center/repositories/${encodeURIComponent(item.id)}/operations/plan`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type":
            "application/json",
        },
        body: JSON.stringify({
          operation,
        }),
      },
    );

    if (!response.ok) {
      let detail = "";

      try {
        const body = await response.json();

        detail = (
          body?.detail
          || body?.message
          || ""
        );
      } catch {
        // Keep generic HTTP error.
      }

      throw new Error(
        detail
        || `planner_http_${response.status}`
      );
    }

    return response.json();
  }

  async function handleRepositoryPlanAction(
    action,
    item,
  ) {
    const operation = (
      resolveRepositoryOperation(
        action,
        item,
      )
    );

    if (!operation) {
      return false;
    }

    try {
      const plan = await (
        planRepositoryOperation(
          item,
          operation,
        )
      );

      const blockers = Array.isArray(
        plan?.blockers
      )
        ? plan.blockers
        : [];

      const blockerText = blockers
        .map(repositoryBlockerText)
        .join("\n");

      const summary = [
        `İşlem: ${repositoryOperationLabel(operation)}`,
        `Dal: ${plan?.branch || "Bilinmiyor"}`,
        `Commit ilişkisi: ${plan?.commitRelation || "Bilinmiyor"}`,
        `Yerel çalışma: ${plan?.worktreeStatus || "Bilinmiyor"}`,
        `Sunucuda fazla: ${plan?.ahead ?? 0}`,
        `GitHub'da fazla: ${plan?.behind ?? 0}`,
      ];

      if (blockerText) {
        summary.push(
          "",
          "İşlem şu anda uygulanamaz:",
          blockerText,
        );
      } else {
        summary.push(
          "",
          "Ön kontrol başarılı.",
          "Bu aşamada yalnız plan gösteriliyor.",
          "Henüz hiçbir Git işlemi çalıştırılmayacak.",
        );
      }

      setModal({
        title:
          repositoryOperationLabel(
            operation,
          ),

        message:
          summary.join("\n"),

        readOnly:
          !plan?.executable,

        confirm:
          plan?.executable
            ? "Onayla ve uygula"
            : "Kapat",

        danger:
          plan?.worktreeStatus
          === "conflicted",

        repositoryPlan: plan,
        repositoryItem: item,
        repositoryOperation: operation,
      });

      return true;

    } catch (error) {
      setModal({
        title:
          "Repository planı alınamadı",

        message:
          error?.message
          || "Planner isteği başarısız oldu.",

        readOnly: true,

        confirm:
          "Kapat",
      });

      return true;
    }
  }

  async function fetchGithubRepositories() {
    try {
      const response = await fetch(
        "/api/v1/repository-center/github/repositories",
        {
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(
          `github_repositories_http_${response.status}`,
        );
      }

      const payload =
        await response.json();

      return {
        payload,
        items:
          normalizeGithubRepositories(
            payload,
          ),
      };
    } catch (error) {
      return {
        payload: null,
        items: [],
        error:
          error instanceof Error
            ? error.message
            : "github_repository_discovery_failed",
      };
    }
  }

  async function scan() {
    if (scanState === "running") return;

    setScanState("running");
    setScanError(null);

    try {
      const health = await serverClient.health();

      setConnections(c => ({
        ...c,
        server: {
          connected: Boolean(health?.ok),
          account:
            authenticatedUser?.display_name ||
            authenticatedUser?.email ||
            "KNSAIX Server",
          endpoint: "KNSAIX yönetim servisi",
          node: "primary",
        },
      }));

      const discovered =
        await serverClient.scanInventory();

      const normalized =
        normalizeInventory(discovered);

      const githubDiscovery =
        await fetchGithubRepositories();

      const merged =
        mergeServerAndGithubInventory(
          normalized,
          githubDiscovery.items,
        );

      setItems(merged);

      if (githubDiscovery.payload) {
        setConnections(c => ({
          ...c,
          github: {
            ...c.github,
            connected: Boolean(
              githubDiscovery.payload?.connected,
            ),
            account:
              githubDiscovery.payload?.account ||
              c.github.account ||
              null,
            repositoryCount:
              Number(
                githubDiscovery.payload
                  ?.total_count || 0,
              ),
            repositoryStatus: "ready",
          },
        }));
      }

      setScanState("done");

      addActivity(
        "ok",
        "Envanter yenilendi",
        githubDiscovery.error
          ? `${normalized.length} sunucu öğesi keşfedildi. GitHub repository envanteri alınamadı.`
          : `${merged.length} öğe; ${githubDiscovery.items.length} GitHub repository kaydıyla birleştirildi.`,
      );

      window.setTimeout(
        () => setScanState("idle"),
        1400,
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "repository_center_discovery_failed";

      setScanError(message);
      setScanState("error");

      setConnections(c => ({
        ...c,
        server: {
          ...c.server,
          connected: false,
        },
      }));

      addActivity(
        "warn",
        "Sunucu envanteri alınamadı",
        message,
      );
    }
  }

  useEffect(() => {
    void scan();
    // İlk gerçek discovery yalnız mount sırasında çalışır.
    // Manuel "Envanteri tara" aynı fonksiyonu tekrar çağırır.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleItem = (id) => {
    const item = items.find(x => x.id === id);
    if (!item) return;

    setModal({
      title: `Durum değişikliği: ${item.name}`,
      body:
        "P1A salt-okunur modda gerçek sunucu durumunu değiştirmiyoruz. Aç/kapat işlemi P1B IAM ve doğrulama katmanından sonra etkinleştirilecek.",
      confirm: "Tamam",
      danger: false,
      readOnly: true,
    });
  };

  const action = (item, actionName) => {
    if (actionName === "assistant") {
      setAssistantItem(item);
      setAssistantOpen(true);
      return;
    }

    if (actionName === "health") {
      void scan();
      return;
    }

    const labels = {
      sync: "Senkronizasyon",
      safe_sync: "Güvenli eşitleme",
      pull: "GitHub'dan alma",
      push: "GitHub'a gönderme",
      update: "Güncelleme",
      remove: "Güvenli kaldırma",
      deploy: "Dağıtım",
      clone_server: "Sunucuya getirme",
      link_github: "GitHub'a bağlama",
      unlink_github: "GitHub bağlantısını kaldırma",
      restart: "Servis yeniden başlatma",
      permissions: "Yetki değişikliği",
      rerun_failed: "Otomasyonu yeniden çalıştırma",
    };

    setModal({
      title: `${labels[actionName] || "İşlem"}: ${item.name}`,
      body:
        "Bu ekran şu anda gerçek sunucu envanterini salt okunur olarak kullanıyor.\n\n" +
        "Bu işlem henüz uygulanmayacak. P1B aşamasında KNSAIX IAM yetkisi, etki analizi, doğrulama ve geri alma sınırları bağlandıktan sonra açılacak.",
      confirm: "Tamam",
      danger: false,
      readOnly: true,
    });
  };

  const createFromSource = ({ mode, value }) => {
    setAddOpen(false);

    addActivity(
      "info",
      "Kurulum henüz kapalı",
      `${value} incelenebilir; gerçek kurulum P1B yetki ve lifecycle bağlantısından sonra açılacak.`,
    );

    setModal({
      title: "P1A salt-okunur mod",
      body:
        "Gerçek envanter bağlantısı aktif. Yeni repo, araç, skill veya MCP kurma işlemleri henüz açılmadı.\n\n" +
        "P1B aşamasında KNSAIX IAM yetkileri ve güvenli kurulum akışı bağlanacak.",
      confirm: "Tamam",
      danger: false,
      readOnly: true,
    });
  };

  const bulkRemove = () => {
    if (!selected.length) return;
    setModal({ title: `${selected.length} öğeyi kaldır`, body: "Seçilen öğelerin bağımlılıkları ve kullanan bileşenleri kontrol edilerek güvenli kaldırma planı hazırlanacak.", confirm: "Güvenli kaldır", danger: true, run: () => { setItems(p => p.filter(x => !selected.includes(x.id))); setSelected([]); addActivity("warn", "Toplu kaldırma tamamlandı", `${selected.length} öğe`); } });
  };

  return <div style={{ minHeight: "100vh", background: T.bg, color: T.text, fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
    <header style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(18,24,27,.96)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${T.border}` }}>
      <div style={{ maxWidth: 1450, margin: "0 auto", padding: "12px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <div style={{ display: "flex", gap: 9, alignItems: "center" }}>
              <div style={{ color: T.accent, fontWeight: 1000, letterSpacing: ".06em", fontSize: 14 }}>KNSAIX</div>
              <div style={{ color: T.text, fontWeight: 900 }}>Repository & Capability Center</div>
              <Help title="Bu ekran ne yapar?">GitHub ve KNSAIX sunucusundaki repo, araç, skill, MCP, agent, servis ve akışları tek envanterde yönetir. Git terimleri Türkçe açıklanır ama gerçek Git/GitHub davranışları korunur.</Help>
            </div>
            <div style={{ color: T.muted, fontSize: 11, marginTop: 4 }}>Tek envanter · GitHub aktif kullanım · sunucu yönetimi · araç ilişkileri · güvenli senkronizasyon</div>
          </div>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            <Btn onClick={() => { setAssistantItem(null); setAssistantOpen(true); }}>KNSAIX Yardımcısı</Btn>
            <Btn tone="info" onClick={scan} disabled={scanState === "running"}>{scanState === "running" ? "Taranıyor…" : scanState === "done" ? "✓ Yenilendi" : "Envanteri tara"}</Btn>
            <Btn tone="accent" onClick={() => setAddOpen(true)}>+ Ekle / Kur</Btn>
          </div>
        </div>
      </div>
    </header>

    <main style={{ maxWidth: 1450, margin: "0 auto", padding: 16 }}>
      <SectionTitle>Bağlantılar</SectionTitle>
      <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginBottom: 16 }}>
        <ConnectionCard
          type="github"
          connected={connections.github.connected}
          account={connections.github.account}
          detail={
            connections.github.connected
              ? `${connections.github.repositoryCount || 0} repository · GitHub App`
              : connections.github.repositoryStatus === "unavailable"
                ? "GitHub durumu alınamadı"
                : "GitHub App bağlanmamış"
          }
          onLogin={() => login("github")}
          onLogout={() => logout("github")}
        />
        <ConnectionCard type="server" connected={connections.server.connected} account={connections.server.account} detail={connections.server.connected ? "Mevcut KNSAIX oturumu · gerçek sunucu envanteri" : "KNSAIX yönetim servisi kontrol ediliyor"} onLogin={() => scan()} onLogout={() => {}} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 8, marginBottom: 16 }}>
        <div style={summaryCard}><Stat label="Repository" value={counts.repos} /></div>
        <div style={summaryCard}><Stat label="Araç / Skill / Servis" value={counts.tools} /></div>
        <div style={summaryCard}><Stat label="Dikkat gereken" value={counts.issues} tone={counts.issues ? "warn" : "ok"} /></div>
        <div style={summaryCard}><Stat label="Güncelleme" value={counts.updates} tone={counts.updates ? "info" : "ok"} /></div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 310px", gap: 12, alignItems: "start" }} className="knsaix-main-grid">
        <section style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
          <nav
            aria-label="Depo ve Araç Merkezi"
            style={{
              display: "flex",
              gap: 4,
              padding: "8px 10px 0",
              borderBottom: `1px solid ${T.border}`,
              background: T.surface2,
            }}
          >
            {[
              ["repositories", "Depolar", counts.repos],
              ["tools", "Araçlar", items.filter(i => [
                "tool",
                "mcp",
              ].includes(i.kind)).length],
              ["skills", "Skills", items.filter(i => i.kind === "skill").length],
            ].map(([id, label, count]) => {
              const active = primaryView === id;

              return (
                <button
                  key={id}
                  type="button"
                  aria-current={active ? "page" : undefined}
                  onClick={() => {
                    setPrimaryView(id);
                    setKind("all");
                    setSelected([]);
                    setExpanded(null);
                  }}
                  style={{
                    border: 0,
                    borderBottom: `2px solid ${active ? T.accent : "transparent"}`,
                    background: "transparent",
                    color: active ? T.text : T.muted,
                    padding: "9px 12px 10px",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: active ? 800 : 650,
                  }}
                >
                  {label}
                  <span
                    style={{
                      marginLeft: 7,
                      color: active ? T.accent : T.faint,
                      fontSize: 10,
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </nav>

          <div style={{ padding: 11, borderBottom: `1px solid ${T.border}`, display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Repo, araç, skill, MCP, agent, servis ara…" style={{ ...inputStyle, flex: "1 1 280px", width: "auto" }} />
            <select value={kind} onChange={e => setKind(e.target.value)} style={selectStyle}>
              <option value="all">Tüm türler</option>
              {Object.entries(KIND_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select value={source} onChange={e => setSource(e.target.value)} style={selectStyle}>
              <option value="all">Tüm kaynaklar</option><option value="github">GitHub</option><option value="server">Sunucu</option>
            </select>
            {selected.length > 0 && <><Pill tone="warn">{selected.length} seçili</Pill><Btn compact tone="danger" onClick={bulkRemove}>Kaldır</Btn></>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "24px 1fr minmax(160px,.42fr) auto auto", gap: 12, padding: "8px 12px", color: T.faint, fontSize: 10, borderBottom: `1px solid ${T.borderSoft}` }}>
            <span /><span>Öğe</span><span>Durum / Kaynak</span><span>Hızlı işlem</span><span>Açık</span>
          </div>
          {filtered.map(item => <InventoryRow key={item.id} item={item} expanded={expanded === item.id} onExpand={() => setExpanded(expanded === item.id ? null : item.id)} onToggle={() => toggleItem(item.id)} onAction={(a) => {
            if (
              item?.kind === "repository"
              && a === "diff"
            ) {
              void handleRepositoryDiffAction(
                item,
              );
              return;
            }

            if (
              item?.kind === "repository"
              && (
                a === "sync"
                || a === "pull"
                || a === "push"
                || a === "safe_sync"
              )
            ) {
              void handleRepositoryPlanAction(
                a,
                item,
              );
              return;
            }

            action(item, a);
          }} selected={selected.includes(item.id)} onSelect={(checked) => setSelected(s => checked ? [...new Set([...s, item.id])] : s.filter(x => x !== item.id))} />)}
          {!filtered.length && <div style={{ padding: 12 }}><Empty>Bu filtreye uyan öğe yok.</Empty></div>}
        </section>

        <aside style={{ display: "grid", gap: 12 }}>
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: 11 }}>
            <SectionTitle right={<Help title="Aktivite">GitHub webhookları, sunucu watcher'ı ve kullanıcı işlemleri aynı zaman çizelgesine düşer.</Help>}>Aktivite</SectionTitle>
            <div style={{ display: "grid", gap: 8 }}>{activity.slice(0, 8).map(a => <div key={a.id} style={{ borderLeft: `2px solid ${a.tone === "ok" ? T.ok : a.tone === "warn" ? T.warn : a.tone === "fail" ? T.danger : T.info}`, paddingLeft: 8 }}><div style={{ color: T.text2, fontSize: 11, fontWeight: 700 }}>{a.title}</div><div style={{ color: T.faint, fontSize: 10, marginTop: 2 }}>{a.detail}</div><div style={{ color: T.faint, fontSize: 9, marginTop: 2 }}>{a.at}</div></div>)}</div>
          </div>
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: 11 }}>
            <SectionTitle>Nasıl çalışır?</SectionTitle>
            {[
              ["1", "GitHub'a giriş yap", "Repo, PR, Issues, Actions ve Security görünür."],
              ["2", "KNSAIX Server'a giriş yap", "Sunucudaki repo ve tüm araçlar otomatik taranır."],
              ["3", "KNSAIX eşleştirir", "GitHub repo ↔ server workspace ↔ araç/skill/servis ilişkileri bulunur."],
              ["4", "Tek panelden yönet", "Kur, kaldır, aç/kapat, eşitle, test et, deploy et ve geri al."],
            ].map(([n,t,d]) => <div key={n} style={{ display: "grid", gridTemplateColumns: "22px 1fr", gap: 7, marginBottom: 9 }}><div style={{ width: 20, height: 20, borderRadius: 5, border: `1px solid ${T.border}`, display: "grid", placeItems: "center", color: T.accent, fontWeight: 900, fontSize: 10 }}>{n}</div><div><div style={{ color: T.text2, fontWeight: 700, fontSize: 11 }}>{t}</div><div style={{ color: T.faint, fontSize: 10, marginTop: 2, lineHeight: 1.45 }}>{d}</div></div></div>)}
          </div>
        </aside>
      </div>
    </main>

    <AddDrawer open={addOpen} onClose={() => setAddOpen(false)} onCreate={createFromSource} />
    <AssistantPanel open={assistantOpen} onClose={() => setAssistantOpen(false)} currentItem={assistantItem} />
    <ConfirmModal modal={modal} onModalChange={setModal} onClose={() => setModal(null)} onConfirm={async () => {
          const currentModal = modal;

          if (
            currentModal?.repositoryCommitItem?.id
          ) {
            const defaultMessage =
              `KNSAIX: ${currentModal.repositoryCommitItem.name} yerel değişiklikleri`;

            const commitMessage = (currentModal.commitMessage || "");

            if (!commitMessage?.trim()) {
              return;
            }

            setModal({
              title: "Commit oluşturuluyor",
              message:
                "Seçtiğiniz repository'deki yerel "
                + "değişiklikler Git commit'e alınıyor. "
                + "GitHub'a henüz gönderilmiyor.",
              readOnly: true,
              confirm: "Bekleyin",
            });

            try {
              const response = await fetch(
                `/api/v1/repository-center/repositories/${encodeURIComponent(currentModal.repositoryCommitItem.id)}/operations/commit`,
                {
                  method: "POST",
                  credentials: "include",
                  headers: {
                    "Content-Type":
                      "application/json",
                  },
                  body: JSON.stringify({
                    message:
                      commitMessage.trim(),
                    files:
                      Array.isArray(
                        currentModal.selectedFiles
                      )
                        ? currentModal.selectedFiles
                        : [],

                  }),
                },
              );

              let payload = {};

              try {
                payload =
                  await response.json();
              } catch {
                payload = {};
              }

              if (!response.ok) {
                throw new Error(
                  payload?.detail
                  || payload?.message
                  || `commit_http_${response.status}`
                );
              }

              const shortCommit =
                payload?.commit
                  ? payload.commit.slice(0, 10)
                  : "";

              setModal({
                title: "Commit oluşturuldu",
                message:
                  `${commitMessage.trim()}\n`
                  + (
                    shortCommit
                      ? `Commit: ${shortCommit}\n\n`
                      : ""
                  )
                  + "Yerel değişiklikler commit edildi. "
                  + "Şimdi Değişiklikler sekmesindeki "
                  + "\"GitHub'a gönder\" düğmesiyle "
                  + "push işlemini güvenli plandan geçirebilirsiniz.",
                readOnly: true,
                confirm: "Tamam",
              });

              addActivity(
                "ok",
                "Repository commit oluşturuldu",
                currentModal.repositoryCommitItem.name,
              );

              await scan();

            } catch (error) {
              setModal({
                title: "Commit oluşturulamadı",
                message:
                  error?.message
                  || "Git commit işlemi başarısız oldu.",
                readOnly: true,
                confirm: "Kapat",
                danger: true,
              });
            }

            return;
          }

          if (
            !currentModal?.repositoryPlan?.executable
            || !currentModal?.repositoryItem?.id
            || !currentModal?.repositoryOperation
          ) {
            setModal(null);
            return;
          }

          setModal({
            title: "İşlem uygulanıyor",
            message:
              "Sunucu mevcut durumu yeniden doğruluyor. "
              + "İşlem yalnız güvenliyse uygulanacak.",
            readOnly: true,
            confirm: "Bekleyin",
          });

          try {
            const response = await fetch(
              `/api/v1/repository-center/repositories/${encodeURIComponent(currentModal.repositoryItem.id)}/operations/execute`,
              {
                method: "POST",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  operation:
                    currentModal.repositoryOperation,
                }),
              },
            );

            let payload = {};

            try {
              payload = await response.json();
            } catch {
              payload = {};
            }

            if (!response.ok) {
              throw new Error(
                payload?.detail
                || payload?.message
                || `execute_http_${response.status}`
              );
            }

            const safeValidation =
              currentModal.repositoryOperation
                === "safe_sync"
              && payload?.result?.validated
              && !payload?.result?.promoted;

            setModal({
              title: safeValidation
                ? "Güvenli eşitleme doğrulandı"
                : "İşlem tamamlandı",
              message: safeValidation
                ? (
                    payload?.result?.message
                    || "İzole doğrulama başarılı."
                  )
                : (
                    `${repositoryOperationLabel(
                      currentModal.repositoryOperation
                    )} tamamlandı.`
                  ),
              readOnly: true,
              confirm: "Tamam",
            });

            addActivity(
              "ok",
              safeValidation
                ? "Repository doğrulandı"
                : "Repository işlemi tamamlandı",
              currentModal.repositoryItem.name,
            );

            await scan();

          } catch (error) {
            setModal({
              title: "İşlem uygulanamadı",
              message:
                error?.message
                || "Repository işlemi başarısız oldu.",
              readOnly: true,
              confirm: "Kapat",
              danger: true,
            });
          }
        }} />

    <style>{`
      * { box-sizing: border-box; }
      button, input, select, textarea { font: inherit; }
      small { color: ${T.faint}; font-size: 9px; }
      @media (max-width: 980px) {
        .knsaix-main-grid { grid-template-columns: 1fr !important; }
      }
      @media (max-width: 720px) {
        header > div, main { padding-left: 10px !important; padding-right: 10px !important; }
      }
    `}</style>
  </div>;
}

const cardStyle = { background: T.surface2, border: `1px solid ${T.borderSoft}`, borderRadius: 6, padding: 10 };
const summaryCard = { ...cardStyle, padding: 12 };
const miniTitle = { color: T.muted, fontSize: 10, textTransform: "uppercase", letterSpacing: ".07em", fontWeight: 800, marginBottom: 7 };
const listItem = { color: T.text2, fontSize: 11, padding: "5px 0", borderBottom: `1px solid ${T.borderSoft}` };
const muted = { color: T.faint, fontSize: 11 };
const inputStyle = { width: "100%", background: "#101619", color: T.text, border: `1px solid ${T.border}`, borderRadius: 5, padding: "8px 9px", outline: "none", fontSize: 12 };
const selectStyle = { background: "#101619", color: T.text2, border: `1px solid ${T.border}`, borderRadius: 5, padding: "7px 8px", fontSize: 11 };
const chipBtn = { background: T.surface2, color: T.text2, border: `1px solid ${T.border}`, borderRadius: 999, padding: "5px 8px", fontSize: 10, cursor: "pointer" };
const tabBtn = (active) => ({ background: active ? T.surface3 : "transparent", color: active ? T.text : T.muted, border: `1px solid ${active ? "#4B5960" : T.border}`, borderRadius: 5, padding: "6px 8px", fontSize: 11, cursor: "pointer" });
const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,.62)", zIndex: 60, display: "grid", placeItems: "center", padding: 16 };
const drawer = { width: 620, maxWidth: "100%", background: "#11171A", border: `1px solid ${T.border}`, borderRadius: 9, boxShadow: "0 28px 80px rgba(0,0,0,.58)", padding: 14 };
